<template>
  <!-- 退会時にエラーが出るため、v-if="user"を付与 -->
  <div v-if="user && !isLoading">
    <div
      :class="['bg', `bg-${product.slug}`]"
      v-if="user.hasRegisteredProfile"
    />
    <div class="mypage py-3">
      <div class="section1">
        <div v-if="canApplyOrHasWorked" class="px-3">
          <ProfileCard
            v-if="avgEvaluation && balance && myApplicationCount"
            :user="user"
            :currentRankLabel="currentRankLabel"
            :avgEvaluation="avgEvaluation"
            :balance="balance"
            :myApplicationCount="myApplicationCount"
            :cancelPercentage="cancelPercentage"
            :suddenCancelPercentage="suddenCancelPercentage"
          />
        </div>
        <VerificationAccountStep
          v-else
          :isLoading="isLoading"
          :user="user"
          :hasBankAccount="hasBankAccount"
        />
      </div>

      <!-- AD: mypage -->
      <Ad adSlot="mypage" class="ad-slot-mypage" />

      <div class="menu-content pt-4 mt-2">
        <MenuLinkGroup label="勤務管理" class="section2">
          <MenuRouterLinkItem
            id="tooltippopup-to-menu-router-link-item-salary"
            label="給与管理"
            :to="{ name: 'Salary' }"
          >
            <template #right>
              <p v-if="balance" class="text-blue text-size-14">
                {{ currency(balance.amount) }}円
              </p>
            </template>
          </MenuRouterLinkItem>

          <!-- 吹き出し -->
          <TooltipPopup
            position="top"
            targetSelector="#tooltippopup-to-menu-router-link-item-salary"
          >
            <div>
              お仕事が完了後、<TooltipHighlightText>いつでも給与引出可能</TooltipHighlightText>！<br />
              引き出し手数料も<TooltipHighlightText>無料</TooltipHighlightText>！
            </div>
          </TooltipPopup>

          <MenuRouterLinkItem
            label="働きたい日"
            :to="{ name: 'DesiredWorkSchedules' }"
          >
            <template #right>
              <p
                v-if="hasDesiredWorkSchedules"
                class="text-size-14"
                data-testid="desired-work-schedules"
              >
                <span
                  :class="[
                    hasDesiredWorkSchedulesThisMonth ? 'text-blue' : 'text-red',
                    'mr-1'
                  ]"
                  >{{ desiredWorkSchedulesThisMonthLabel }}</span
                >
                <span
                  :class="
                    hasDesiredWorkSchedulesFromNextMonth
                      ? 'text-blue'
                      : 'text-red'
                  "
                >
                  {{ desiredWorkSchedulesFromNextMonthLabel }}
                </span>
              </p>
              <p
                v-else
                class="text-size-14"
                data-testid="desired-work-schedules-empty"
              >
                <span class="text-red">未登録</span>
              </p>
            </template>
          </MenuRouterLinkItem>
          <MenuRouterLinkItem label="今月のランク" :to="{ name: 'Rank' }">
            <template #right>
              <RankIcon14 :rank="user.rank" />
              <p class="text-mild text-size-14 ml-1">
                {{ currentRankLabel }}
              </p>
            </template>
          </MenuRouterLinkItem>
          <MenuRouterLinkItem label="勤務評価" :to="{ name: 'MyReview' }">
            <template #right>
              <p v-if="avgEvaluation" class="text-blue text-size-14 ml-1">
                {{ evaluationRound(avgEvaluation.totalAverage) }}点 ({{
                  avgEvaluation.count || 0
                }}件)
              </p>
            </template>
          </MenuRouterLinkItem>
          <MenuRouterLinkItem
            label="信用スコア"
            :to="{ name: 'OrdersCancelPolicyDisplay' }"
          >
            <template #right>
              <p class="text-blue text-size-14 ml-1">
                {{ user.creditScore }}pt
              </p>
            </template>
          </MenuRouterLinkItem>
        </MenuLinkGroup>

        <MenuLinkGroup label="ポイント・キャンペーン" class="section3">
          <MenuRouterLinkItem :to="{ name: 'MyPoint' }">
            <template #left>
              <p class="text-size-14">ポイント</p>
            </template>
            <template #right>
              <p class="text-blue text-size-14">
                {{ currency(user.caisukePoint) }}pt
              </p>
            </template>
          </MenuRouterLinkItem>
          <MenuRouterLinkItem
            label="招待キャンペーン"
            :to="{ name: 'ReferPromotion' }"
          />
        </MenuLinkGroup>

        <MenuLinkGroup label="アカウント">
          <MenuRouterLinkItem label="認証" :to="{ name: 'MypageAccount' }">
            <template #right>
              <VerifiedStatusLabel
                v-if="hasNotice"
                :text="userVerifiedStatusLabel"
                :type="userVerifiedStatusLabelType"
              />
            </template>
          </MenuRouterLinkItem>
          <MenuRouterLinkItem label="設定" :to="{ name: 'AccountInfo' }" />
        </MenuLinkGroup>

        <MenuLinkGroup label="教育">
          <MenuRouterLinkItem label="研修" :to="{ name: 'Training' }" />
        </MenuLinkGroup>

        <MenuLinkGroup label="サポート">
          <MenuAnchorLinkItem
            label="FAQ・お問い合わせ"
            :href="externalLink.faq"
          />
          <MenuAnchorLinkItem
            label="転職の報告"
            :href="externalLink.reportJobChange"
          />
          <MenuAnchorLinkItem
            label="ご意見・ご要望"
            :href="externalLink.anonymousFeedbackForm"
          />
        </MenuLinkGroup>

        <div
          v-if="isDevMenuVisible"
          class="section5 px-3"
          data-testid="dev-menu"
        >
          <p class="text-mild text-size-14 text-weight-500">
            開発メニュー(prod, demoでは表示されません)
          </p>
          <div class="bg-white border border-outline-color px-3 mt-1 round-4">
            <div
              role="button"
              class="d-flex justify-content-between align-items-center border-outline-color py-3"
              @click="clearLocalStorage"
            >
              <p class="text-size-14">LocalStorageクリア</p>
              <div class="d-flex justify-content-end align-items-center">
                <FeatherIcon
                  icon="chevron-right"
                  size="1x"
                  class="text-gray-05 ml-2"
                />
              </div>
            </div>
          </div>
          <div class="bg-white border border-outline-color px-3 mt-1 round-4">
            <div
              role="button"
              class="d-flex justify-content-between align-items-center border-outline-color py-3"
              @click="changeProduct('caitech_care')"
            >
              <p class="text-size-14">「カイテク（介護・看護）」アプリに変更</p>
              <div class="d-flex justify-content-end align-items-center">
                <FeatherIcon
                  icon="chevron-right"
                  size="1x"
                  class="text-gray-05 ml-2"
                />
              </div>
            </div>
          </div>
          <div class="bg-white border border-outline-color px-3 mt-1 round-4">
            <div
              role="button"
              class="d-flex justify-content-between align-items-center border-outline-color py-3"
              @click="changeProduct('caitech_pharmacist')"
            >
              <p class="text-size-14">「カイテク薬剤師」アプリに変更</p>
              <div class="d-flex justify-content-end align-items-center">
                <FeatherIcon
                  icon="chevron-right"
                  size="1x"
                  class="text-gray-05 ml-2"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="px-3 mt-2">
        <p class="text-mild text-size-10">Version No: {{ versionNo }}</p>
        <p class="text-mild text-size-10">Build Version: {{ buildVersion }}</p>
      </div>
    </div>
    <AppFooter />
    <BlockBack />
  </div>
</template>

<script lang="ts">
import { fetchVersionFromDocument } from "@/app-updator";
import { accountService } from "@/app/services/AccountService";
import { applicationService } from "@/app/services/ApplicationService";
import { orderService } from "@/app/services/OrderService";
import { salaryService } from "@/app/services/SalaryService";
import AppButton from "@/components/atoms/button/AppButton.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import TooltipHighlightText from "@/components/atoms/other/TooltipHighlightText.vue";
import RankIcon14 from "@/components/molecules/other/RankIcon14.vue";
import TooltipPopup from "@/components/molecules/other/TooltipPopup.vue";
import Ad from "@/components/organisms/common/Ad.vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import MenuAnchorLinkItem from "@/components/organisms/mypage/MenuAnchorLinkItem.vue";
import MenuLinkGroup from "@/components/organisms/mypage/MenuLinkGroup.vue";
import MenuRouterLinkItem from "@/components/organisms/mypage/MenuRouterLinkItem.vue";
import ProfileCard from "@/components/organisms/mypage/ProfileCard.vue";
import VerificationAccountStep from "@/components/organisms/mypage/VerificationAccountStep.vue";
import VerifiedStatusLabel from "@/components/organisms/mypage/VerifiedStatusLabel.vue";
import { VUE_APP_ENV } from "@/config/VueAppEnv";
import {
  APP_URL_ANONYMOUS_FEEDBACK_FORM,
  APP_URL_FAQ,
  APP_URL_REPORT_JOB_CHANGE
} from "@/const/app";
import rankMap from "@/models/rank";
import verifiedStatus from "@/models/verifiedStatus";
import { authStore } from "@/store/auth";
import { deviceModule } from "@/store/device";
import { Balance } from "@/types/balance/Balance";
import { DesiredWorkSchedule } from "@/types/desired_work_schedule/DesiredWorkSchedule";
import { AvailableCount } from "@/types/order/AvailableCount";
import { UserQualification } from "@/types/qualification/UserQualification";
import { TotalAverageEvaluationByOffice } from "@/types/worker/TotalAverageEvaluationByOffice";
import {
  addMonths,
  endOfMonth,
  formatFromUnixTimestamp,
  now,
  parseISO,
  startOfMonth
} from "@/utils/date";
import { evaluationRound } from "@/utils/evaluationRound";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { Component, Vue } from "vue-facing-decorator";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const cordova: any;

@Component({
  components: {
    FeatherIcon,
    AppFooter,
    BlockBack,
    ProfileCard,
    AppButton,
    MenuRouterLinkItem,
    MenuAnchorLinkItem,
    MenuLinkGroup,
    VerifiedStatusLabel,
    VerificationAccountStep,
    RankIcon14,
    Ad,
    TooltipPopup,
    TooltipHighlightText
  }
})
export default class MypageIndex extends Vue {
  filledStarImageSrc = require("@/assets/svg/star/filled_star_yellow.svg");
  filledStarHalfImageSrc = require("@/assets/svg/star/filled_star_half_yellow.svg");
  avgEvaluation: TotalAverageEvaluationByOffice | null = null;
  balance: Balance | null = null;
  qualifications: UserQualification[] = [];
  externalLink = {
    faq: APP_URL_FAQ,
    anonymousFeedbackForm: APP_URL_ANONYMOUS_FEEDBACK_FORM,
    reportJobChange: APP_URL_REPORT_JOB_CHANGE
  };
  myApplicationCount: AvailableCount | null = null;
  hasBankAccount = false;
  isLoading = false;
  cancelPercentage = 0;
  suddenCancelPercentage = 0;
  desiredWorkSchedulesThisMonth: DesiredWorkSchedule[] = [];
  desiredWorkSchedulesFromNextMonth: DesiredWorkSchedule[] = [];

  get versionNo() {
    return deviceModule.versionNo;
  }
  get buildVersion() {
    return fetchVersionFromDocument();
  }
  get product() {
    return authStore.product;
  }
  get user() {
    return authStore.user!;
  }
  get isDevMenuVisible() {
    const env = VUE_APP_ENV;
    return !(env === "prod" || env === "demo");
  }

  get currentRankLabel() {
    const rankObj = rankMap.find(rank => {
      return rank.id === this.user?.rank;
    });
    if (rankObj) {
      return rankObj.name;
    } else {
      return "レギュラー";
    }
  }

  get hasRejectedQualification() {
    return this.qualifications.some(qualification => {
      return qualification.status === verifiedStatus.REJECTED;
    });
  }

  get hasUnverifiedQualification() {
    return this.qualifications.some(qualification => {
      return qualification.status === verifiedStatus.UNVERIFIED;
    });
  }

  get hasVerifiedQualification() {
    return this.qualifications.some(qualification => {
      return qualification.status === verifiedStatus.VERIFIED;
    });
  }

  get hasVerifiedAccountInfo() {
    return this.user.verifiedStatus === verifiedStatus.VERIFIED;
  }

  get isRegisteredBusinessInfo() {
    return this.user.experiences.length > 0;
  }

  get hasNotice() {
    return !this.isAlreadyRegistered || this.hasRejectedVerification;
  }

  get isAlreadyRegistered() {
    return this.hasBankAccount && this.isRegisteredBusinessInfo;
  }

  get hasRejectedVerification() {
    return (
      this.user.verifiedStatus === verifiedStatus.REJECTED ||
      this.hasRejectedQualification
    );
  }

  get hasUnverifiedVerification() {
    return (
      this.user.verifiedStatus === verifiedStatus.UNVERIFIED ||
      this.hasUnverifiedQualification
    );
  }

  get userVerifiedStatusLabel() {
    if (!this.isAlreadyRegistered) return "未登録";
    if (this.hasRejectedVerification) return "差戻し";
    if (this.hasUnverifiedVerification) return "審査中";
    return "";
  }

  get userVerifiedStatusLabelType() {
    if (!this.isAlreadyRegistered) return "danger";
    if (this.hasRejectedVerification) return "danger";
    if (this.hasUnverifiedVerification) return "notice";
    return "notice";
  }

  get canApplyOrHasWorked() {
    if (this.user.workCount > 0) {
      return true;
    }
    return (
      this.hasVerifiedAccountInfo &&
      this.isRegisteredBusinessInfo &&
      this.hasVerifiedQualification &&
      this.hasBankAccount
    );
  }

  async created() {
    try {
      this.$loading.show();
      this.isLoading = true;
      // アカウント情報を更新
      await accountService.updateUserData();
      await Promise.all([
        this.fetchAvgEvaluation(),
        this.fetchBalance(),
        this.fetchQualification(),
        this.fetchAvailableCountAndLimit(),
        this.fetchHasBankAccount(),
        this.fetchCancelRate(),
        this.fetchDesiredWorkSchedules()
      ]);
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
      this.isLoading = false;
    }
  }

  async onClickdNoticeBtn() {
    await this.$router.push({ name: "MypageAccount" });
  }

  evaluationRound(value: number | null) {
    return evaluationRound(value);
  }

  async fetchAvgEvaluation() {
    if (!this.user) return;
    this.avgEvaluation = await orderService.fetchTotalEvaluationByOffice(
      this.user.id
    );
  }

  async fetchBalance() {
    this.balance = await salaryService.fetchBalance();
  }

  async fetchQualification() {
    this.qualifications = (await accountService.fetchQualifications()).results;
  }

  async fetchAvailableCountAndLimit() {
    this.myApplicationCount = await applicationService.fetchAvailableCount();
  }

  currency(value: number | string) {
    return Number(value).toLocaleString();
  }

  async fetchHasBankAccount() {
    this.hasBankAccount = await salaryService.fetchHasBankAccount();
  }

  async fetchCancelRate() {
    const res = await accountService.fetchCancelRate();
    this.cancelPercentage = Math.floor(res.cancelRate * 100);
    this.suddenCancelPercentage = Math.floor(res.suddenCancelRate * 100);
  }

  get hasDesiredWorkSchedulesThisMonth() {
    return this.desiredWorkSchedulesThisMonth.length > 0;
  }

  get hasDesiredWorkSchedulesFromNextMonth() {
    return this.desiredWorkSchedulesFromNextMonth.length > 0;
  }

  get hasDesiredWorkSchedules() {
    return (
      this.hasDesiredWorkSchedulesThisMonth ||
      this.hasDesiredWorkSchedulesFromNextMonth
    );
  }

  get desiredWorkSchedulesThisMonthLabel() {
    return this._desiredWorkSchedulesLabel(
      this.desiredWorkSchedulesThisMonth,
      now()
    );
  }

  get desiredWorkSchedulesFromNextMonthLabel() {
    return this._desiredWorkSchedulesLabel(
      this.desiredWorkSchedulesFromNextMonth,
      addMonths(now(), 1),
      true
    );
  }

  _desiredWorkSchedulesLabel(
    schedules: DesiredWorkSchedule[],
    month: number,
    isAfterAll = false
  ) {
    const monthLabel = formatFromUnixTimestamp(
      month,
      isAfterAll ? "M月以降" : "M月"
    );
    return schedules.length === 0
      ? `${monthLabel}(未登録)`
      : `${monthLabel}(${schedules.length}件)`;
  }

  async fetchDesiredWorkSchedules() {
    const nowEpoch = now();
    const thisMonthStart = startOfMonth(nowEpoch);
    const thisMonthEnd = endOfMonth(nowEpoch);
    const nextMonthStart = startOfMonth(addMonths(nowEpoch, 1));

    const schedules = await accountService.fetchDesiredWorkSchedules();
    this.desiredWorkSchedulesThisMonth = schedules.filter(s => {
      const target = parseISO(s.date);
      return thisMonthStart <= target && target <= thisMonthEnd;
    });
    this.desiredWorkSchedulesFromNextMonth = schedules.filter(s => {
      const target = parseISO(s.date);
      return nextMonthStart <= target;
    });
  }

  clearLocalStorage() {
    localStorage.clear();
    location.reload();
  }

  changeProduct(productSlug: string) {
    authStore.changeProduct(productSlug);
    location.reload();
  }
}
</script>
<style lang="scss" scoped>
.bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 160px;
  border-radius: 0 0 40px 40px;

  &.bg-caitech_care {
    background-color: $secondary;
  }

  &.bg-caitech_pharmacist {
    background-color: #03a99d;
  }
}

.mypage {
  position: relative;

  .menu-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .section1 {
    // iPhone のノッチ対応
    padding-top: env(safe-area-inset-top, 0);
  }

  .section3 {
    .link {
      text-decoration: underline;
    }
  }
}

.list-indent {
  padding-left: 1em;
  text-indent: -1em;
}

.ad-slot-mypage {
  margin: 16px 16px -16px;
}
</style>
